"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, CircleCheck as CheckCircle2, Clock, MapPin, PhoneCall, ShieldCheck, Truck, Wrench } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
const steps = [{
  stepNumber: "01",
  badgeLabel: "Step 01",
  title: "Tell Us Your Location",
  description: "Provide your address or neighborhood in Jonesboro or surrounding Clayton County so we can pinpoint your nearest available technician.",
  highlight: "Real-time route optimization",
  icon: "location"
}, {
  stepNumber: "02",
  badgeLabel: "Step 02",
  title: "Tell Us What's Happening",
  description: "Explain your emergency—burst pipes, sewer line backup, or broken water heater. Our diagnostics specialist confirms the urgent tools needed.",
  highlight: "Direct diagnosis & guidance",
  icon: "issue"
}, {
  stepNumber: "03",
  badgeLabel: "Step 03",
  title: "Request the Appropriate Service",
  description: "Confirm your priority request. A licensed plumber rolls out immediately in a fully equipped service vehicle ready to solve the issue on site.",
  highlight: "Immediate on-site arrival",
  icon: "dispatch"
}];
export default function HowItWorks() {
  const shouldReduceMotion = useReducedMotion();
  const getStepIcon = (iconType: string) => {
    switch (iconType) {
      case "location":
        return <MapPin className="size-6 text-primary" aria-hidden="true" />;
      case "issue":
        return <Wrench className="size-6 text-primary" aria-hidden="true" />;
      case "dispatch":
        return <Truck className="size-6 text-primary" aria-hidden="true" />;
      default:
        return <ShieldCheck className="size-6 text-primary" aria-hidden="true" />;
    }
  };
  return <section id="howitworks" data-nav="light" className="relative w-full bg-background text-foreground py-20 md:py-28 lg:py-32 overflow-hidden">
      {/* Decorative subtle background elements */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden" aria-hidden="true">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[400px] bg-secondary/40 rounded-full blur-3xl opacity-60" />
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <motion.div initial={shouldReduceMotion ? {
        opacity: 1,
        y: 0
      } : {
        opacity: 0,
        y: 24
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: "-80px"
      }} transition={{
        duration: 0.5,
        ease: "easeOut"
      }} className="text-center max-w-3xl mx-auto mb-16 md:mb-20">
          <Badge variant="outline" className="border-accent/40 bg-accent/10 text-foreground font-semibold px-3.5 py-1 text-xs uppercase tracking-wider rounded-full mb-4">
            Simple 3-Step Process
          </Badge>
          <h2 className="font-default text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground">
            How Emergency Dispatch Works
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground font-default leading-relaxed max-w-2xl mx-auto">
            From sudden leaks to full plumbing failures, our streamlined emergency response system gets a licensed professional to your Jonesboro property fast.
          </p>
        </motion.div>

        {/* 3-Step Process Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-8 relative">
          {/* Visual Connecting Line for desktop */}
          <div className="hidden md:block absolute top-1/3 left-12 right-12 h-0.5 border-t-2 border-dashed border-border -z-0" aria-hidden="true" />

          {steps.map((step, index) => <motion.div key={step.stepNumber} data-index={index} initial={shouldReduceMotion ? {
          opacity: 1,
          y: 0
        } : {
          opacity: 0,
          y: 32
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: "-60px"
        }} transition={{
          duration: 0.5,
          delay: index * 0.15,
          ease: "easeOut"
        }} className="relative flex flex-col h-full">
              <Card className="flex-1 bg-card text-card-foreground border border-border shadow-md rounded-2xl p-6 sm:p-8 flex flex-col justify-between hover:shadow-lg transition-[box-shadow,border-color] duration-300 relative overflow-hidden group">
                {/* Top Accent Gradient Edge */}
                <div className="absolute top-0 left-0 right-0 h-1 bg-primary group-hover:bg-accent transition-colors duration-300" />

                <div>
                  {/* Step Header: Icon Container + Number Badge */}
                  <div className="flex items-center justify-between mb-6">
                    {/* Squircle Icon Container */}
                    <div className="size-14 rounded-2xl bg-secondary flex items-center justify-center shadow-xs border border-border">
                      {getStepIcon(step.icon)}
                    </div>

                    {/* Step Number Badge */}
                    <div className="flex items-center gap-1.5 bg-primary text-primary-foreground font-bold text-sm px-3 py-1 rounded-full shadow-xs border border-accent/40">
                      <span className="text-accent text-xs font-mono font-semibold">STEP</span>
                      <span>{step.stepNumber}</span>
                    </div>
                  </div>

                  <CardHeader className="p-0 mb-3">
                    <CardTitle className="font-default text-xl sm:text-2xl font-bold tracking-tight text-card-foreground">
                      {step.title}
                    </CardTitle>
                  </CardHeader>

                  <CardContent className="p-0">
                    <CardDescription className="font-default text-sm sm:text-base text-muted-foreground leading-relaxed mb-6">
                      {step.description}
                    </CardDescription>
                  </CardContent>
                </div>

                {/* Bottom Highlight Feature */}
                <div className="pt-4 border-t border-border/80 flex items-center gap-2 text-xs sm:text-sm font-medium text-foreground">
                  <CheckCircle2 className="size-4 text-accent shrink-0" aria-hidden="true" />
                  <span>{step.highlight}</span>
                </div>
              </Card>
            </motion.div>)}
        </div>

        {/* Conversion Cluster */}
        <motion.div initial={shouldReduceMotion ? {
        opacity: 1,
        y: 0
      } : {
        opacity: 0,
        y: 24
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: "-40px"
      }} transition={{
        duration: 0.5,
        delay: 0.35,
        ease: "easeOut"
      }} className="mt-16 sm:mt-20 p-6 sm:p-10 rounded-2xl bg-card border border-border shadow-lg flex flex-col lg:flex-row items-center justify-between gap-8 text-center lg:text-left max-w-5xl mx-auto">
          <div className="max-w-xl">
            <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-accent-foreground bg-accent/20 px-3 py-1 rounded-full mb-3">
              <Clock className="size-3.5" aria-hidden="true" />
              <span>Available 24/7 / 365 Days</span>
            </div>
            <h3 className="font-default text-2xl sm:text-3xl font-bold text-card-foreground tracking-tight">
              Facing an Active Plumbing Disaster?
            </h3>
            <p className="mt-2 text-muted-foreground font-default text-sm sm:text-base">
              Speak immediately with a Jonesboro emergency technician. No hold times or automated queues.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row items-center gap-3.5 w-full sm:w-auto shrink-0">
            <Button asChild size="lg" className="w-full sm:w-auto bg-accent text-accent-foreground hover:bg-accent/90 shadow-md font-bold text-base h-12 px-7 rounded-lg">
              <a href="tel:4708479076" className="inline-flex items-center justify-center gap-2">
                <PhoneCall className="size-5" aria-hidden="true" />
                <span>Call (470) 847-9076</span>
              </a>
            </Button>

            <Button asChild size="lg" className="w-full sm:w-auto bg-primary text-primary-foreground hover:bg-primary/90 shadow-md font-semibold text-base h-12 px-6 rounded-lg">
              <Link to="/contact/" className="inline-flex items-center justify-center gap-2">
                <span>Book Service Online</span>
                <ArrowRight className="size-4" aria-hidden="true" />
              </Link>
            </Button>
          </div>
        </motion.div>
      </div>
    </section>;
}